import { useMemo, useState, type ReactNode } from 'react';
import {
  Activity,
  BookOpen,
  Calculator,
  Calendar,
  ChevronDown,
  Compass,
  Download,
  GraduationCap,
  Heart,
  Laptop,
  Lightbulb,
  Music,
  Palette,
  Sun,
} from 'lucide-react';
import { BreakIcon } from '@/components/timetable/BreakIcon';
import { ExamSlotCard } from '@/components/timetable/ExamSlotCard';
import { examsInPeriod } from '@/components/timetable/examPlacement';
import { ExamDayMark, WeekExams, examsByDay } from '@/components/timetable/WeekExams';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import {
  WEEKDAY_LABEL,
  periodRange,
} from '@/features/teacher/components/timetable/timetableCopy';
import { cn } from '@/lib/utils';
import type { StudentTimetable } from '@/types/student';
import { formatLongDay, todayIso } from '../utils';

/**
 * Returns theme-compliant colors and icon for a subject.
 * Integrates with the application's light/dark design system while keeping subjects distinctly identifiable.
 */
function getSubjectTheme(subjectName: string): {
  card: string;
  dot: string;
  icon: ReactNode;
} {
  const norm = subjectName.toLowerCase();

  // Mathematics (Emerald)
  if (norm.includes('math')) {
    return {
      card: 'bg-emerald-500/10 border-emerald-500/25 dark:bg-emerald-950/40 dark:border-emerald-500/30 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-500/15',
      dot: 'bg-emerald-600 dark:bg-emerald-400',
      icon: <Calculator className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-300" />,
    };
  }

  // English (Purple)
  if (norm.includes('eng')) {
    return {
      card: 'bg-purple-500/10 border-purple-500/25 dark:bg-purple-950/40 dark:border-purple-500/30 text-purple-800 dark:text-purple-200 hover:bg-purple-500/15',
      dot: 'bg-purple-600 dark:bg-purple-400',
      icon: <BookOpen className="h-4 w-4 shrink-0 text-purple-600 dark:text-purple-300" />,
    };
  }

  // Environmental Science / Science / Physics / Chemistry / Biology (Cyan)
  if (
    norm.includes('evs') ||
    norm.includes('environ') ||
    norm.includes('sci') ||
    norm.includes('bio') ||
    norm.includes('chem') ||
    norm.includes('phy')
  ) {
    return {
      card: 'bg-cyan-500/10 border-cyan-500/25 dark:bg-cyan-950/40 dark:border-cyan-500/30 text-cyan-800 dark:text-cyan-200 hover:bg-cyan-500/15',
      dot: 'bg-cyan-600 dark:bg-cyan-400',
      icon: <Compass className="h-4 w-4 shrink-0 text-cyan-600 dark:text-cyan-300" />,
    };
  }

  // Hindi / Sanskrit / Regional Languages (Amber/Orange)
  if (
    norm.includes('hindi') ||
    norm.includes('sanskrit') ||
    norm.includes('telugu') ||
    norm.includes('tamil') ||
    norm.includes('marathi') ||
    norm.includes('urdu') ||
    norm.includes('lang')
  ) {
    return {
      card: 'bg-amber-500/10 border-amber-500/25 dark:bg-amber-950/40 dark:border-amber-500/30 text-amber-800 dark:text-amber-200 hover:bg-amber-500/15',
      dot: 'bg-amber-600 dark:bg-amber-400',
      icon: (
        <span className="font-extrabold text-xs shrink-0 text-amber-600 dark:text-amber-300 leading-none px-0.5">
          अ
        </span>
      ),
    };
  }

  // Computer / IT / AI (Blue)
  if (
    norm.includes('comp') ||
    norm.includes('it') ||
    norm.includes('ai') ||
    norm.includes('tech') ||
    norm.includes('cyber')
  ) {
    return {
      card: 'bg-blue-500/10 border-blue-500/25 dark:bg-blue-950/40 dark:border-blue-500/30 text-blue-800 dark:text-blue-200 hover:bg-blue-500/15',
      dot: 'bg-blue-600 dark:bg-blue-400',
      icon: <Laptop className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" />,
    };
  }

  // Art & Craft (Pink/Magenta)
  if (norm.includes('art') || norm.includes('craft') || norm.includes('draw')) {
    return {
      card: 'bg-pink-500/10 border-pink-500/25 dark:bg-pink-950/40 dark:border-pink-500/30 text-pink-800 dark:text-pink-200 hover:bg-pink-500/15',
      dot: 'bg-pink-600 dark:bg-pink-400',
      icon: <Palette className="h-4 w-4 shrink-0 text-pink-600 dark:text-pink-300" />,
    };
  }

  // Physical Education / Sports (Gold/Yellow)
  if (
    norm.includes('phys') ||
    norm.includes('pe') ||
    norm.includes('sport') ||
    norm.includes('game') ||
    norm.includes('pt')
  ) {
    return {
      card: 'bg-yellow-500/10 border-yellow-500/25 dark:bg-yellow-950/40 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-500/15',
      dot: 'bg-yellow-600 dark:bg-yellow-400',
      icon: <Activity className="h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-300" />,
    };
  }

  // Music (Rose/Red)
  if (norm.includes('music') || norm.includes('vocal') || norm.includes('dance')) {
    return {
      card: 'bg-rose-500/10 border-rose-500/25 dark:bg-rose-950/40 dark:border-rose-500/30 text-rose-800 dark:text-rose-200 hover:bg-rose-500/15',
      dot: 'bg-rose-600 dark:bg-rose-400',
      icon: <Music className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-300" />,
    };
  }

  // Moral Science / Social Studies / History (Indigo)
  if (
    norm.includes('moral') ||
    norm.includes('value') ||
    norm.includes('social') ||
    norm.includes('history') ||
    norm.includes('civic')
  ) {
    return {
      card: 'bg-indigo-500/10 border-indigo-500/25 dark:bg-indigo-950/40 dark:border-indigo-500/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-500/15',
      dot: 'bg-indigo-600 dark:bg-indigo-400',
      icon: <Heart className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-300" />,
    };
  }

  // Library (Sky)
  if (norm.includes('lib') || norm.includes('read')) {
    return {
      card: 'bg-sky-500/10 border-sky-500/25 dark:bg-sky-950/40 dark:border-sky-500/30 text-sky-800 dark:text-sky-200 hover:bg-sky-500/15',
      dot: 'bg-sky-600 dark:bg-sky-400',
      icon: <BookOpen className="h-4 w-4 shrink-0 text-sky-600 dark:text-sky-300" />,
    };
  }

  // Life Skills / General (Teal)
  if (norm.includes('life') || norm.includes('skill')) {
    return {
      card: 'bg-teal-500/10 border-teal-500/25 dark:bg-teal-950/40 dark:border-teal-500/30 text-teal-800 dark:text-teal-200 hover:bg-teal-500/15',
      dot: 'bg-teal-600 dark:bg-teal-400',
      icon: <Lightbulb className="h-4 w-4 shrink-0 text-teal-600 dark:text-teal-300" />,
    };
  }

  // Default Fallback
  return {
    card: 'bg-accent-soft border-accent-line text-ink hover:bg-accent/15',
    dot: 'bg-accent',
    icon: <BookOpen className="h-4 w-4 shrink-0 text-accent" />,
  };
}

/**
 * Downloads the timetable as CSV.
 */
function downloadTimetableCsv(timetable: StudentTimetable | null) {
  if (!timetable) return;
  const days = timetable.workingDays ?? [];
  const periods = timetable.periods ?? [];

  const headers = ['Time Slot', ...days.map((d) => WEEKDAY_LABEL[d] ?? d)];
  const rows: string[][] = [headers];

  periods.forEach((period) => {
    const timeRange = periodRange(period.startTime, period.endTime);
    if (period.kind === 'BREAK') {
      const label = period.label || 'Break';
      rows.push([timeRange, ...days.map(() => label)]);
    } else {
      const row = [
        timeRange,
        ...days.map((day) => {
          const holiday = timetable.days?.find((d) => d.dayOfWeek === day && d.holiday)?.holiday;
          if (holiday) return `Holiday: ${holiday}`;
          const entry = timetable.entries?.find(
            (e) => e.dayOfWeek === day && e.periodUuid === period.uuid,
          );
          if (!entry) return 'Free';
          return `${entry.subject.name}${entry.teacher ? ` (${entry.teacher})` : ''}`;
        }),
      ];
      rows.push(row);
    }
  });

  const csvContent =
    '\uFEFF' +
    rows
      .map((row) =>
        row
          .map((cell) => {
            const escaped = (cell || '').replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(','),
      )
      .join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const sectionName = timetable.section?.displayName
    ? timetable.section.displayName.replace(/[^a-zA-Z0-9_-]/g, '_')
    : 'Class';
  link.setAttribute('href', url);
  link.setAttribute('download', `Timetable_${sectionName}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface ParentTimetableGridProps {
  timetable: StudentTimetable | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  childName?: string;
  className?: string;
  /** The week chooser the page owns — drawn where the controls bar starts. */
  weekControls?: ReactNode;
}

export function ParentTimetableGrid({
  timetable,
  isLoading,
  error,
  onRetry,
  childName,
  className,
  weekControls,
}: ParentTimetableGridProps) {
  const [viewMode, setViewMode] = useState<'weekly' | 'list'>('weekly');
  const days = timetable?.workingDays ?? [];
  const periods = timetable?.periods ?? [];
  const teachingPeriods = periods.filter((period) => period.kind === 'TEACHING');
  const hasWeek = days.length > 0 && teachingPeriods.length > 0;

  // "Today is Monday, 26 May 2026" — the school's day in India, not the
  // device's, which is the day the server dates the week from.
  const todayFormatted = useMemo(() => `Today is ${formatLongDay(todayIso())}`, []);

  /**
   * The days this week's timetable actually applies on. A timetable that starts
   * on a Wednesday teaches nothing on Monday and Tuesday, and its lessons must
   * not be drawn there. Missing on an older response means every day.
   */
  const coveredDays = useMemo(
    () =>
      new Set(
        (timetable?.days ?? [])
          .filter((day) => day.inTimetable !== false)
          .map((day) => day.dayOfWeek),
      ),
    [timetable?.days],
  );

  const examsOnDay = useMemo(() => examsByDay(timetable?.days), [timetable?.days]);

  /** "26 Oct" under each column, so the reader knows which week this is. */
  const dateByDay = useMemo(
    () =>
      new Map(
        (timetable?.days ?? []).map((day) => [
          day.dayOfWeek,
          new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
            new Date(`${day.date}T00:00:00Z`),
          ),
        ]),
      ),
    [timetable?.days],
  );

  const holidayByDay = useMemo(() => {
    return new Map(
      (timetable?.days ?? [])
        .filter((day) => day.holiday !== null)
        .map((day) => [day.dayOfWeek, day.holiday as string]),
    );
  }, [timetable?.days]);

  // Subject Teacher List (Image 2). Every teacher a subject is taught by — a
  // subject split between two teachers names both, not whichever came first.
  const subjectTeacherList = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (timetable?.entries ?? []).forEach((entry) => {
      const teachers = map.get(entry.subject.name) ?? new Set<string>();
      if (entry.teacher) teachers.add(entry.teacher);
      map.set(entry.subject.name, teachers);
    });
    return Array.from(map.entries())
      .map(([subject, teachers]) => ({
        subject,
        teacher: teachers.size > 0 ? [...teachers].sort().join(', ') : 'Not allotted',
      }))
      .sort((a, b) => a.subject.localeCompare(b.subject));
  }, [timetable?.entries]);

  // Unique Subjects for Legend (Image 1 bottom-right)
  const uniqueSubjects = useMemo(() => {
    const names = new Set<string>();
    (timetable?.entries ?? []).forEach((e) => names.add(e.subject.name));
    return Array.from(names).sort();
  }, [timetable?.entries]);

  const displayClassName = className || timetable?.section?.displayName || 'Class';

  return (
    <div className="flex flex-col gap-5">
      {/* 1. TOP HEADER BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Class Timetable</h1>
          <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
            View your child's class schedule, subjects and timings.
          </p>
        </div>

        {/* Right side: Class Name Pill & Today's Date */}
        <div className="flex flex-wrap items-center gap-2.5 sm:self-start">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-2 border border-line shadow-2xs text-xs font-semibold text-ink">
            <GraduationCap className="h-4 w-4 text-accent shrink-0" />
            <span className="text-ink-2 text-base">Class:</span>
            <span className="text-ink font-bold text-sm">{displayClassName}</span>
            {timetable?.classTeacher && (
              <span className="text-ink-2 font-medium ml-1 text-sm">
                • {timetable.classTeacher}
              </span>
            )}
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-2 border border-line shadow-2xs text-xs font-semibold text-ink-2">
            <Calendar className="h-3.5 w-3.5 text-accent shrink-0" />
            <span className="text-ink-2 text-sm">{todayFormatted}</span>
          </div>
        </div>
      </div>

      {/* 2. CONTROLS BAR: Week, View Mode Switcher, Download */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Left: which week. A real choice of dates, where a dropdown of
            terms used to sit that changed nothing on the grid. */}
        {weekControls}

        {/* Right: Weekly / List View Toggle & Download Button */}
        <div className="flex items-center gap-3">
          {/* View Switcher */}
          <div className="inline-flex items-center p-1 rounded-xl bg-surface-2 border border-line shadow-2xs">
            <button
              type="button"
              onClick={() => setViewMode('weekly')}
              className={cn(
                'px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer text-sm',
                viewMode === 'weekly'
                  ? 'bg-accent text-white shadow-2xs'
                  : 'text-ink-2 hover:text-ink',
              )}
            >
              Weekly View
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={cn(
                'px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer text-sm', 
                viewMode === 'list'
                  ? 'bg-accent text-white shadow-2xs'
                  : 'text-ink-2 hover:text-ink',
              )}
            >
              List View
            </button>
          </div>

          {/* Download Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => downloadTimetableCsv(timetable)}
            className="inline-flex items-center gap-2 rounded-xl border border-line hover:border-accent-line text-ink hover:text-accent text-xs font-bold shadow-2xs cursor-pointer"
          >
            <Download className="h-4 w-4 text-accent shrink-0" />
            <span className="text-sm">Download</span>
            <ChevronDown className="h-3.5 w-3.5 text-ink-3 shrink-0" />
          </Button>
        </div>
      </div>

      {/* With a grid the papers sit in its periods; without one they still need showing. */}
      {!isLoading && !error && !hasWeek && <WeekExams days={timetable?.days} />}

      {/* 3. MAIN TIMETABLE CARD */}
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-5 shadow-2xs">
        {isLoading && <SkeletonLines count={8} />}

        {!isLoading && error && (
          <div className="flex flex-col items-start gap-3 py-6">
            <Alert
              tone="critical"
              title={childName ? `Could not load ${childName}'s timetable` : 'Could not load timetable'}
            >
              {error.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && !hasWeek && (
          <p className="py-16 text-center text-sm font-semibold text-ink-2">
            {timetable?.section
              ? `${childName ? `${childName}'s` : 'The'} class timetable has not been arranged yet.`
              : childName
                ? `${childName} has not been placed in a class yet.`
                : 'No timetable found for this class.'}
          </p>
        )}

        {/* --- WEEKLY VIEW MATRIX --- */}
        {!isLoading && !error && hasWeek && viewMode === 'weekly' && (
          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full table-fixed min-w-[760px] lg:min-w-full border-collapse text-left text-xs">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {/* Time Header */}
                  <th className="w-28 sm:w-32 px-3 py-3 text-center border-r border-line text-sm font-bold text-ink-2">
                    Time
                  </th>

                  {/* Days Headers */}
                  {days.map((day) => {
                    const label = WEEKDAY_LABEL[day] ?? day;
                    return (
                      <th
                        key={day}
                        className="p-3 text-center border-r border-line last:border-r-0 text-sm font-bold text-ink"
                      >
                        {label}
                        {dateByDay.get(day) && (
                          <span className="block text-xs font-medium text-ink-3">
                            {dateByDay.get(day)}
                          </span>
                        )}
                        <ExamDayMark papers={examsOnDay.get(day) ?? []} />
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody className="divide-y divide-line">
                {periods.map((period) => {
                  // BREAK ROW (Full Width Span)
                  if (period.kind === 'BREAK') {
                    const label = period.label || 'Break';

                    return (
                      <tr
                        key={period.uuid}
                        className="bg-surface-2/70 border-y border-dashed border-line"
                      >
                        <td className="px-3 py-2 text-center border-r border-line font-semibold text-xs text-ink-3 whitespace-nowrap">
                          {periodRange(period.startTime, period.endTime)}
                        </td>
                        <td colSpan={days.length} className="py-2 px-4 text-center">
                          <div className="inline-flex items-center justify-center gap-2 text-base font-semibold text-ink-2">
                            <BreakIcon label={label} className="h-3.5 w-3.5 text-amber shrink-0" />
                            <span>{label}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  // TEACHING PERIOD ROW
                  return (
                    <tr
                      key={period.uuid}
                      className="bg-surface hover:bg-surface-2/40 transition-colors"
                    >
                      {/* Time Slot Column */}
                      <td className="px-3 py-2.5 text-center border-r border-line align-middle bg-surface-2/30">
                        <span className="font-semibold text-sm text-ink-2 whitespace-nowrap block">
                          {periodRange(period.startTime, period.endTime)}
                        </span>
                      </td>

                      {/* Day Columns */}
                      {days.map((day) => {
                        const holiday = holidayByDay.get(day);

                        if (holiday) {
                          // One cell per row, never a tall cell spanning them:
                          // the break rows run the full width, and a span across
                          // them pushed every later cell into the wrong column.
                          const isFirstRow = period.uuid === teachingPeriods[0]?.uuid;
                          return (
                            <td
                              key={day}
                              className="p-1.5 sm:p-2 border-r border-line last:border-r-0 align-middle text-center"
                            >
                              <div className="h-10 rounded-xl border border-dashed border-amber/30 bg-amber/10 px-2 flex items-center justify-center gap-1.5 min-w-0">
                                {isFirstRow ? (
                                  <>
                                    <Sun className="h-3.5 w-3.5 text-amber shrink-0" />
                                    <span className="font-bold text-xs text-ink truncate">{holiday}</span>
                                  </>
                                ) : (
                                  <span className="text-2xs text-ink-3">Closed</span>
                                )}
                              </div>
                            </td>
                          );
                        }

                        const match = coveredDays.has(day)
                          ? timetable?.entries.find(
                              (entry) => entry.dayOfWeek === day && entry.periodUuid === period.uuid,
                            )
                          : undefined;

                        // Papers sat in this period, as the teacher's week places them.
                        const examCards = examsInPeriod(examsOnDay.get(day) ?? [], {
                          timeStart: period.startTime,
                          timeEnd: period.endTime,
                        }).map((paper) => <ExamSlotCard key={paper.uuid} exam={paper} />);

                        // A free period with a paper in it shows the paper alone.
                        if (!match && examCards.length > 0) {
                          return (
                            <td
                              key={day}
                              className="p-1.5 sm:p-2 border-r border-line last:border-r-0 align-middle min-w-0"
                            >
                              <div className="flex flex-col gap-1.5">{examCards}</div>
                            </td>
                          );
                        }

                        if (!match) {
                          return (
                            <td
                              key={day}
                              className="p-1.5 sm:p-2 border-r border-line last:border-r-0 align-middle"
                            >
                              <div className="h-10 rounded-xl bg-surface-2/40 border border-dashed border-line/70" />
                            </td>
                          );
                        }

                        const theme = getSubjectTheme(match.subject.name);

                        return (
                          <td
                            key={day}
                            className="p-1.5 sm:p-2 border-r border-line last:border-r-0 align-middle min-w-0"
                          >
                            {/* The lesson stays beside the paper, as on the teacher's week. */}
                            {examCards.length > 0 && <div className="mb-1.5 flex flex-col gap-1.5">{examCards}</div>}
                            <div
                              title={`${match.subject.name}${match.teacher ? ` • ${match.teacher}` : ''}`}
                              className={cn(
                                'h-10 px-3 py-2 rounded-xl border flex items-center gap-2.5 shadow-2xs transition-all hover:scale-[1.02] cursor-default min-w-0',
                                theme.card,
                              )}
                            >
                              <span className="shrink-0">{theme.icon}</span>
                              <span className="font-bold text-sm leading-tight truncate whitespace-nowrap">
                                {match.subject.name}
                              </span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* --- LIST VIEW (DAY-BY-DAY LIST) --- */}
        {!isLoading && !error && hasWeek && viewMode === 'list' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {days.map((day) => {
              const label = WEEKDAY_LABEL[day] ?? day;
              const holiday = holidayByDay.get(day);
              const dayEntries = coveredDays.has(day)
                ? (timetable?.entries ?? []).filter((entry) => entry.dayOfWeek === day)
                : [];
              const dayExams = examsOnDay.get(day) ?? [];

              return (
                <div
                  key={day}
                  className="rounded-xl border border-line bg-surface p-4 flex flex-col gap-3 shadow-2xs"
                >
                  <div className="flex items-center justify-between pb-2 border-b border-line">
                    <h3 className="text-sm font-bold text-ink">{label}</h3>
                    <span className="text-2xs font-semibold text-ink-3">
                      {holiday ? 'Holiday' : `${dayEntries.length} Classes`}
                      {dayExams.length > 0 && <span className="ml-1.5 font-bold text-amber">· Exam</span>}
                    </span>
                  </div>

                  {holiday ? (
                    <div className="py-6 flex flex-col items-center justify-center gap-1.5 text-center">
                      <Sun className="h-5 w-5 text-amber" />
                      <p className="font-bold text-xs text-ink">{holiday}</p>
                      <p className="text-2xs text-ink-3">Institute closed</p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {/* The day's papers first, earliest first: on an exam day
                          they are what the child is going in for. */}
                      {dayExams.map((paper) => (
                        <ExamSlotCard key={paper.uuid} exam={paper} />
                      ))}
                      {periods.map((period) => {
                        const timeStr = periodRange(period.startTime, period.endTime);
                        if (period.kind === 'BREAK') {
                          return (
                            <div
                              key={period.uuid}
                              className="px-3 py-1.5 rounded-lg bg-surface-2 border border-line flex items-center justify-between text-xs text-ink-3"
                            >
                              <span>{timeStr}</span>
                              <span className="font-medium text-ink-2">{period.label || 'Break'}</span>
                            </div>
                          );
                        }

                        const entry = dayEntries.find((e) => e.periodUuid === period.uuid);
                        if (!entry) return null;
                        const theme = getSubjectTheme(entry.subject.name);

                        return (
                          <div
                            key={period.uuid}
                            className="px-3 py-2 rounded-xl bg-surface border border-line flex items-center justify-between gap-3"
                          >
                            <span className="text-xs font-semibold text-ink-3 shrink-0">
                              {timeStr}
                            </span>
                            <div
                              className={cn(
                                'px-2.5 py-1 rounded-lg border flex items-center gap-2 text-xs font-bold truncate',
                                theme.card,
                              )}
                            >
                              {theme.icon}
                              <span className="truncate">{entry.subject.name}</span>
                            </div>
                            <span className="text-xs text-ink-2 truncate text-right">
                              {entry.teacher || 'Not allotted'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. BOTTOM SECTION: Important Note & Subjects Legend */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        {/* IMPORTANT NOTE CARD (Image 1 Bottom-Left) */}
        <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-5 shadow-2xs flex items-center justify-between gap-4 relative overflow-hidden">
          <div className="flex items-start gap-4 z-10">
            {/* Square Outline Box with Calendar Icon */}
            <div className="h-12 w-12 rounded-xl border border-accent-line bg-accent-soft flex items-center justify-center shrink-0">
              <Calendar className="h-6 w-6 text-accent" />
            </div>

            <div>
              <h4 className="text-base font-bold text-accent">Important Note</h4>
              <p className="mt-1 text-xs text-ink-2 leading-relaxed max-w-sm">
                Timetable may be subject to change.
              </p>
            </div>
          </div>

          {/* Isometric Books & Clock Vector Art Illustration */}
          <div className="shrink-0 hidden sm:block opacity-85 select-none pointer-events-none" aria-hidden>
            <svg
              width="110"
              height="95"
              viewBox="0 0 110 95"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="drop-shadow-xs"
            >
              {/* Soft Drop Shadow Base */}
              <ellipse cx="55" cy="85" rx="42" ry="7" fill="currentColor" className="text-line-strong" fillOpacity="0.4" />

              {/* Book 1 (Bottom) */}
              <path d="M22 68 L70 54 L92 63 L44 77 Z" fill="currentColor" className="text-slate-400 dark:text-slate-600" />
              <path d="M22 68 L44 77 L44 82 L22 73 Z" fill="currentColor" className="text-slate-500 dark:text-slate-700" />
              <path d="M44 77 L92 63 L92 68 L44 82 Z" fill="currentColor" className="text-slate-200 dark:text-slate-300" />

              {/* Book 2 (Middle) */}
              <path d="M26 56 L74 42 L90 50 L42 64 Z" fill="currentColor" className="text-indigo-400 dark:text-indigo-700" />
              <path d="M26 56 L42 64 L42 69 L26 61 Z" fill="currentColor" className="text-indigo-500 dark:text-indigo-800" />
              <path d="M42 64 L90 50 L90 55 L42 69 Z" fill="currentColor" className="text-slate-100 dark:text-slate-200" />

              {/* Book 3 (Top) */}
              <path d="M30 45 L76 33 L90 39 L44 51 Z" fill="currentColor" className="text-accent-line" />
              <path d="M30 45 L44 51 L44 56 L30 50 Z" fill="currentColor" className="text-accent" />
              <path d="M44 51 L90 39 L90 44 L44 56 Z" fill="currentColor" className="text-slate-100 dark:text-slate-300" />

              {/* Standing Clock */}
              <g transform="translate(68, 12)">
                <circle cx="16" cy="16" r="16" fill="currentColor" className="text-accent-soft" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="16" cy="16" r="13" fill="currentColor" className="text-surface" />
                <circle cx="16" cy="16" r="1.5" fill="currentColor" className="text-accent" />
                <path d="M16 16 L16 8" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
                <path d="M16 16 L22 16" stroke="currentColor" className="text-accent" strokeWidth="1.7" strokeLinecap="round" />
                <path d="M10 31 L22 31" stroke="currentColor" className="text-accent" strokeWidth="2" strokeLinecap="round" />
                <path d="M13 29 L10 32" stroke="currentColor" className="text-accent" strokeWidth="1.5" />
                <path d="M19 29 L22 32" stroke="currentColor" className="text-accent" strokeWidth="1.5" />
              </g>
            </svg>
          </div>
        </div>

        {/* SUBJECTS LEGEND CARD (Image 1 Bottom-Right) */}
        <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col justify-between">
          <div>
            <h4 className="text-base font-bold text-ink mb-3.5">Subjects Legend</h4>

            {uniqueSubjects.length === 0 ? (
              <p className="text-xs text-ink-3 italic">No subjects loaded</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5 text-xs">
                {uniqueSubjects.map((subjectName) => {
                  const theme = getSubjectTheme(subjectName);
                  return (
                    <div key={subjectName} className="flex items-center gap-2 truncate">
                      <span
                        className={cn('h-2.5 w-2.5 rounded-full shrink-0 shadow-2xs', theme.dot)}
                      />
                      <span className="truncate text-ink-2 font-medium text-sm">
                        {subjectName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 5. SUBJECT TEACHER DETAILS CARD (Image 2) */}
      <div className="rounded-2xl border border-line bg-surface p-5 sm:p-6 shadow-2xs">
        <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-line">
          <h4 className="text-base font-bold text-ink tracking-tight">
            Subject Teacher Details
          </h4>
          <span className="text-xs font-bold text-accent bg-accent-soft border border-accent-line px-3.5 py-1 rounded-full shadow-2xs">
            {subjectTeacherList.length} Subjects
          </span>
        </div>

        {subjectTeacherList.length === 0 ? (
          <p className="text-xs text-ink-3 italic py-2">
            No subject teacher allocations recorded yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2.5 text-xs">
            {subjectTeacherList.map(({ subject, teacher }) => (
              <div
                key={subject}
                className="flex items-baseline justify-between gap-3 py-1.5 border-b border-line/60 "
              >
                <span className="font-bold text-ink text-sm truncate max-w-[55%]">
                  {subject}
                </span>
                <span className="text-ink-3 font-bold shrink-0">:</span>
                <span
                  className="font-medium text-ink-2 text-sm truncate text-right max-w-[42%]"
                  title={teacher}
                >
                  {teacher}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
